/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import {
  GET_TICKET_FETES,
  HOLD_TICKETS,
  REDUCE_TICKET_HOLD,
  RELEASE_TICKET_HOLD,
  GET_MY_TICKET_ORDERS,
  PAY_FOR_TICKETS,
  PAY_COSTUME_AND_TICKETS,
  VERIFY_TICKET_PAYMENT,
  GET_BOX_TICKET_ORDERS,
  PAY_TICKETS_AT_MAS_CAMP,
} from "../graphql";
import {
  ITicketFete,
  IHoldTicketsInput,
  ITicketLineInput,
  ITicketOrder,
  IPayForTicketsInput,
  IPayCostumeAndTicketsInput,
  IPayTicketResult,
  IVerifyTicketPaymentResult,
} from "../../../pages/Costume/components/SelectTicketsComp/types";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ITicketServiceMethods extends IPluginMethodMap {
  getTicketFetes(): Promise<ITicketFete[]>;
  holdTickets(data: IHoldTicketsInput): Promise<ITicketOrder>;
  reduceTicketHold(
    ticketOrderId: string,
    lines: ITicketLineInput[]
  ): Promise<ITicketOrder>;
  releaseTicketHold(ticketOrderId: string): Promise<any>;
  getMyTicketOrders(boxId?: string): Promise<ITicketOrder[]>;
  payForTickets(data: IPayForTicketsInput): Promise<IPayTicketResult>;
  payCostumeAndTickets(
    data: IPayCostumeAndTicketsInput
  ): Promise<IPayTicketResult>;
  verifyTicketPayment(txId: string): Promise<IVerifyTicketPaymentResult>;
  getBoxTicketOrders(boxId: string): Promise<ITicketOrder[]>;
  payTicketsAtMasCamp(data: {
    boxId: string;
    ticketOrderId: string;
    allocations?: { costumeValueId: string; amount: number }[];
    email?: string;
  }): Promise<any>;
}

export class TicketService implements IAgentPlugin {
  readonly methods: ITicketServiceMethods;

  constructor() {
    this.methods = {
      getTicketFetes: this.getTicketFetes.bind(this),
      holdTickets: this.holdTickets.bind(this),
      reduceTicketHold: this.reduceTicketHold.bind(this),
      releaseTicketHold: this.releaseTicketHold.bind(this),
      getMyTicketOrders: this.getMyTicketOrders.bind(this),
      payForTickets: this.payForTickets.bind(this),
      payCostumeAndTickets: this.payCostumeAndTickets.bind(this),
      verifyTicketPayment: this.verifyTicketPayment.bind(this),
      getBoxTicketOrders: this.getBoxTicketOrders.bind(this),
      payTicketsAtMasCamp: this.payTicketsAtMasCamp.bind(this),
    };
  }

  private getClient() {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule?.getClient("graphql-gateway");
    if (!client) {
      throw new Error("GraphQL client 'graphql-gateway' not found");
    }
    return client;
  }

  async getTicketFetes(): Promise<ITicketFete[]> {
    try {
      const client = this.getClient();
      const res = await client.query({
        query: GET_TICKET_FETES,
        fetchPolicy: "network-only",
      });

      return res?.data?.getTicketFetes || [];
    } catch (err: any) {
      console.warn("TicketService.getTicketFetes error:", err?.message || err);
      throw err;
    }
  }

  async holdTickets(data: IHoldTicketsInput): Promise<ITicketOrder> {
    try {
      const client = this.getClient();
      const res = await client.mutate({
        mutation: HOLD_TICKETS,
        variables: { data },
        fetchPolicy: "no-cache",
      });

      return res?.data?.holdTickets;
    } catch (err: any) {
      console.error("TicketService.holdTickets error:", err?.message || err);
      throw err;
    }
  }

  async reduceTicketHold(
    ticketOrderId: string,
    lines: ITicketLineInput[]
  ): Promise<ITicketOrder> {
    try {
      const client = this.getClient();
      const res = await client.mutate({
        mutation: REDUCE_TICKET_HOLD,
        variables: { ticketOrderId, lines },
        fetchPolicy: "no-cache",
      });

      return res?.data?.reduceTicketHold;
    } catch (err: any) {
      console.error(
        "TicketService.reduceTicketHold error:",
        err?.message || err
      );
      throw err;
    }
  }

  async releaseTicketHold(ticketOrderId: string): Promise<any> {
    try {
      const client = this.getClient();
      const res = await client.mutate({
        mutation: RELEASE_TICKET_HOLD,
        variables: { ticketOrderId },
        fetchPolicy: "no-cache",
      });

      return res?.data?.releaseTicketHold;
    } catch (err: any) {
      console.error(
        "TicketService.releaseTicketHold error:",
        err?.message || err
      );
      throw err;
    }
  }

  async getMyTicketOrders(boxId?: string): Promise<ITicketOrder[]> {
    try {
      const client = this.getClient();
      const variables: any = {};
      if (boxId) {
        variables.boxId = boxId;
      }

      const res = await client.query({
        query: GET_MY_TICKET_ORDERS,
        variables,
        fetchPolicy: "network-only",
      });

      return res?.data?.getMyTicketOrders || [];
    } catch (err: any) {
      console.warn(
        "TicketService.getMyTicketOrders error:",
        err?.message || err
      );
      throw err;
    }
  }

  // Staff (#740): a Box's TicketOrders.
  async getBoxTicketOrders(boxId: string): Promise<ITicketOrder[]> {
    const client = this.getClient();
    const res = await client.query({
      query: GET_BOX_TICKET_ORDERS,
      variables: { boxId },
      fetchPolicy: "network-only",
    });
    return res?.data?.getBoxTicketOrders || [];
  }

  // Staff (#740): Mas Camp cash for the held tickets.
  async payTicketsAtMasCamp(data: {
    boxId: string;
    ticketOrderId: string;
    allocations?: { costumeValueId: string; amount: number }[];
    email?: string;
  }): Promise<any> {
    const client = this.getClient();
    const res = await client.mutate({
      mutation: PAY_TICKETS_AT_MAS_CAMP,
      variables: { data },
      fetchPolicy: "no-cache",
    });
    return res?.data?.payTicketsAtMasCamp;
  }

  async payForTickets(data: IPayForTicketsInput): Promise<IPayTicketResult> {
    try {
      const client = this.getClient();
      const res = await client.mutate({
        mutation: PAY_FOR_TICKETS,
        variables: { data },
        fetchPolicy: "no-cache",
      });

      return res?.data?.payForTickets;
    } catch (err: any) {
      console.error("TicketService.payForTickets error:", err?.message || err);
      throw err;
    }
  }

  async payCostumeAndTickets(
    data: IPayCostumeAndTicketsInput
  ): Promise<IPayTicketResult> {
    try {
      const client = this.getClient();
      const res = await client.mutate({
        mutation: PAY_COSTUME_AND_TICKETS,
        variables: { data },
        fetchPolicy: "no-cache",
      });

      return res?.data?.payCostumeAndTickets;
    } catch (err: any) {
      console.error(
        "TicketService.payCostumeAndTickets error:",
        err?.message || err
      );
      throw err;
    }
  }

  async verifyTicketPayment(txId: string): Promise<IVerifyTicketPaymentResult> {
    try {
      const client = this.getClient();
      const res = await client.mutate({
        mutation: VERIFY_TICKET_PAYMENT,
        variables: { txId },
        fetchPolicy: "no-cache",
      });

      return res?.data?.verifyTicketPayment;
    } catch (err: any) {
      console.error(
        "TicketService.verifyTicketPayment error:",
        err?.message || err
      );
      throw err;
    }
  }
}
